import { ConsumerAddress, Country } from '@afterpay/types'
import { createSelector, unwrapResult } from '@reduxjs/toolkit'
import { CreditFacilityStatuses, ErrorCode, IdentityCheckStatus, SubscriptionStatus } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { accountService } from '@afterpaytouch/portal-api/consumer/account'
import {
  accountLookup,
  check2faDob,
  CheckoutAppSelector,
  confirmVerifyAccountMobile,
  ConsumerState,
  getConsumerName,
  registrationErrors,
  setCheckoutStatus,
  setVerifyAccountMobileRequired,
  useAppDispatch,
  useAppSelector,
  useAuthentication,
  useCheckoutConfirm2faFlow,
  useCheckoutError,
  useEfaFlow,
  useIsApplePaylater,
  useIsCashApp,
  usePasswordResetFlow,
  useRegistration,
  useRegistrationFlow,
  useSecurity2faFlow,
  verifyAccountMobile,
} from '../'
import { Route, usePostLoginRoute } from '../../config/router'
import {
  CheckoutStatus,
  ConsumerFacilityCohort,
  IdentityCheckMetadata,
  PaymentMethodType,
  RegistrationStatus,
  SupportedLocale,
  VerificationError,
  VerificationFlow,
  VerificationFlowContext,
  VerificationFlowRoute,
  VerificationMethod,
} from '../../model'
import { identityService } from '@afterpaytouch/portal-api/identity'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { isNullOrUndefined } from '@afterpay/utils'
import { CashEligibilitySignals } from '@afterpaytouch/portal-api/consumer/lookup'

/* Selectors */

export const consumerSelector: CheckoutAppSelector<ConsumerState> = (state) => state.consumer

export const emailSelector: CheckoutAppSelector<string> = createSelector(consumerSelector, (state) => state.email ?? '')

// @ts-expect-error: OPERATION BLEED STOPPER
export const preferredLocale: CheckoutAppSelector<SupportedLocale> = createSelector(consumerSelector, (state) => state.preferredLocale)

export const preferredPaymentMethod: CheckoutAppSelector<PaymentMethodType | undefined> = createSelector(
  consumerSelector,
  (state) => state.preferredPaymentMethod
)

export const consumerNameSelector: CheckoutAppSelector<string> = createSelector(consumerSelector, (state) => getConsumerName(state.profile))

export const consumerLoginIdentitySelector: CheckoutAppSelector<string> = createSelector(consumerSelector, (state) => state.loginIdentityError ?? '')

export const consumerAccountCountrySelector: CheckoutAppSelector<Country | undefined> = createSelector(consumerSelector, (state) => state.accountCountry)

export const smsVerificationEnabledSelector: CheckoutAppSelector<{ smsVerificationEnabled: boolean }> = createSelector(consumerSelector, (state) => ({
  smsVerificationEnabled: state.smsVerificationEnabled,
}))

export const creditCheckConsentedSelector: CheckoutAppSelector<boolean | undefined> = createSelector(consumerSelector, (state) => state.creditCheckConsented)

export const marketingOptInSelector: CheckoutAppSelector<SubscriptionStatus | undefined> = createSelector(consumerSelector, (state) => state.marketingOptIn)

export const identityCheckMetaDataSelector: CheckoutAppSelector<IdentityCheckMetadata | undefined> = createSelector(
  consumerSelector,
  (state) => state.identityCheckMetadata
)

export const cashtagSelector: CheckoutAppSelector<string | undefined | null> = createSelector(consumerSelector, (state) => state.cashtag)

export const {
  endpoints: { identityVerify },
} = identityService

export const cashEligibilitySignalsSelector: CheckoutAppSelector<CashEligibilitySignals | null> = createSelector(
  consumerSelector,
  (state) => state.cashEligibilitySignals
)

export const cashCreationDateSelector: CheckoutAppSelector<number | undefined> = createSelector(consumerSelector, (state) => state.cashCreationDate)

export const shadowAccountExistsSelector: CheckoutAppSelector<boolean | undefined> = createSelector(consumerSelector, (state) => state.shadowAccountExists)

/* React Hooks */

export const useConsumerSelector = (): ConsumerState => useAppSelector(consumerSelector)

export const useConsumerName = (): string => useAppSelector(consumerNameSelector)

export const useConsumerEmail = (): string => useAppSelector(emailSelector)

export const useLoginIdentityError = (): string => useAppSelector(consumerLoginIdentitySelector)

export const useConsumerAccountCountry = (): Country | undefined => useAppSelector(consumerAccountCountrySelector)

export const usePreferredPaymentMethod = (): PaymentMethodType | undefined => useAppSelector(preferredPaymentMethod)

export const useSMSEnabled = (): boolean => useAppSelector(smsVerificationEnabledSelector).smsVerificationEnabled

export const useConsumerPreferredLocale = (): SupportedLocale => useAppSelector(preferredLocale)

export const useConsumerContactAddress = (): ConsumerAddress | null | undefined => useConsumerSelector()?.profile?.contactAddress

export const useCreditCheckConsented = (): boolean | undefined => useAppSelector(creditCheckConsentedSelector)

export const useMarketingOptIn = (): SubscriptionStatus | undefined => useAppSelector(marketingOptInSelector)

export const useVerifyAccountMobileFlow = (): VerificationFlow => {
  const dispatch = useAppDispatch()
  const { mobile } = useConsumerSelector()
  const postLoginRoute = usePostLoginRoute()

  const sendCode = async (): Promise<void> => {
    if (isNullOrUndefined(mobile)) {
      return
    }

    try {
      unwrapResult(await dispatch(verifyAccountMobile({ mobile })))
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(registrationErrors[errorCode])
    }
  }
  const checkCode = async (smsCode: string): Promise<VerificationFlowRoute> => {
    try {
      if (!isNullOrUndefined(mobile)) {
        unwrapResult(await dispatch(confirmVerifyAccountMobile({ mobile, smsCode })))
      }
      const account = unwrapResult(await dispatch(accountLookup()))
      dispatch(setCheckoutStatus(CheckoutStatus.Loaded))

      return {
        route: postLoginRoute,
        locale: account.preferredLocale,
      }
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(registrationErrors[errorCode])
    }
  }

  const checkDob = async (dateOfBirth: Date, lastName: string): Promise<void> => {
    if (isNullOrUndefined(mobile)) {
      return
    }

    try {
      unwrapResult(
        await dispatch(
          check2faDob({
            mobile,
            dateOfBirth,
            lastName,
          })
        )
      )
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(registrationErrors[errorCode])
    }
  }

  return {
    sendCode,
    checkCode,
    context: VerificationFlowContext.VerifyMobile,
    allowLogout: true,
    autoSendCode: false,
    enabled: {
      SMS: true,
      EMAIL: false,
    },
    revealMobile: true,
    previousRoute: Route.ADD_MOBILE,
    initialMethod: VerificationMethod.SMS,
    prefetchRoutes: [Route.ADD_MOBILE, Route.LOGGED_IN],
    checkDob,
  }
}

export const useIsPartialVerified = (): boolean => {
  const { identityCheckStatus } = useConsumerSelector()
  return identityCheckStatus === IdentityCheckStatus.PARTIAL_VERIFIED
}

export const useConsumerFeatureFlagReady = (): boolean => {
  const consumer = useConsumerSelector()
  return consumer?.hasFeatureFlagsReady ?? false
}

export const useUnverifiedMobileRoute = (): (() => Route) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isSmsEnabled = useSMSEnabled()
  const dispatch = useAppDispatch()
  const isApplepayLaterFlow = useIsApplePaylater()
  const verifiedMobileErrorRoute = isSmsEnabled && !isApplepayLaterFlow ? Route.ADD_MOBILE : Route.TERMINAL_ERROR

  return (): Route => {
    dispatch(setVerifyAccountMobileRequired(true))
    logEvent(TrackingEvent.TRIGGER_UNVERIFIED_MOBILE, { smsEnabled: isSmsEnabled })
    return verifiedMobileErrorRoute
  }
}

export const useOpenExistingFacility = (): boolean => {
  const { latestFacilityStatus } = useConsumerSelector()

  return latestFacilityStatus === CreditFacilityStatuses.Open
}

export const useVerificationFlow = (): { flow: VerificationFlow; showCashTerms: boolean } => {
  const auth = useAuthentication()
  const securityVerificationFlow = useSecurity2faFlow()
  const resetPasswordVerificationFlow = usePasswordResetFlow()
  const checkoutConfirm2faFlow = useCheckoutConfirm2faFlow()
  const verifyAccountMobileFlow = useVerifyAccountMobileFlow()
  const registrationVerificationFlow = useRegistrationFlow()
  const registration = useRegistration()
  const efaFlow = useEfaFlow()
  const { verifyAccountMobileRequired = false } = useConsumerSelector()
  const checkoutError = useCheckoutError()
  const isRegistrationFlow = registration.status >= RegistrationStatus.Created
  const isCashApp = useIsCashApp()
  let showCashTerms = false
  let flow: VerificationFlow
  if (isRegistrationFlow) {
    flow = registrationVerificationFlow
  } else if (verifyAccountMobileRequired) {
    // error verify mobile need to go here
    flow = verifyAccountMobileFlow
  } else if (checkoutError?.errorCode === ErrorCode.CheckoutConfirm2FARequired || checkoutError?.errorCode === ErrorCode.CheckoutConfirm2FARequiredNoEmail) {
    flow = checkoutConfirm2faFlow
  } else if (auth.verificationRequired) {
    flow = securityVerificationFlow
    showCashTerms = isCashApp
  } else if (checkoutError?.errorCode === ErrorCode.ExtendedFraudAlert) {
    flow = efaFlow
  } else {
    // Create password / Reset password uses this flow
    flow = resetPasswordVerificationFlow
  }
  return {
    flow,
    showCashTerms,
  }
}

export const useIdentityCheckMetadata = (): IdentityCheckMetadata | undefined => useAppSelector(identityCheckMetaDataSelector)

export const useCashtag = (): string | undefined | null => useAppSelector(cashtagSelector)

export const useCashEligibilitySignals = (): CashEligibilitySignals | null => {
  return useAppSelector(cashEligibilitySignalsSelector)
}

export const useCashCreationDate = (): number | undefined => useAppSelector(cashCreationDateSelector)

export const useShadowAccountExists = (): boolean | undefined => useAppSelector(shadowAccountExistsSelector)

/** An undefined consumer facility cohort indicates either:
 * 1. Region is not applicable (outside of AU)
 * 2. We don't have enough information yet (start checkout has not occurred)
 */
export const useConsumerFacilityCohort = (): ConsumerFacilityCohort | undefined => {
  const { consumerFacilityCohort } = useAppSelector(consumerSelector)

  return consumerFacilityCohort
}

export const {
  useUpdateAccountMutation,
  useUnsuitabilityQuestionsMutation,
  useCreditContractMutation,
  useLazyGetCreditContractDocumentQuery,
  endpoints: { updateAccount },
} = accountService
